import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Star, Calendar } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  CarouselApi,
} from "@/components/ui/carousel";
import { MentorTierBadge } from "@/components/MentorTierBadge";
import { useMentors } from "@/hooks/useMentors";
import TruncatedCredits from "@/components/TruncatedCredits";
import { safeSetItem } from "@/lib/safe-storage";

const defaultAvatar = '/lovable-uploads/1c000ebd-7faf-4987-a62e-8c04a9a373a2.png';

interface MentorCarouselProps {
  mentors?: any[];
  showTitle?: boolean;
  onBookMentor?: (mentor: any) => void;
}

const MentorCarousel = ({ mentors: propMentors, showTitle = true, onBookMentor }: MentorCarouselProps) => {
  const navigate = useNavigate();
  const [mentorApi, setMentorApi] = useState<CarouselApi>();
  const { mentors: hookMentors, loading, error } = useMentors();
  
  // Use prop mentors if provided, otherwise use hook mentors
  const rawMentors = propMentors || hookMentors;

  // Filter and sort mentors - exclude those without profile pictures
  // 1. BLAQOUT first always
  // 2. Pro tier mentors, sorted by rating (highest first)  
  // 3. Non-pro tier mentors, sorted by rating (highest first)
  // 4. If not rated, sort alphabetically
  const sortedMentors = [...rawMentors]
    .filter(mentor => mentor.profile_image_url && mentor.profile_image_url.trim() !== '')
    .sort((a, b) => {
    // BLAQOUT always comes first
    if (a.first_name === 'BLAQOUT') return -1;
    if (b.first_name === 'BLAQOUT') return 1;
    
    // Separate pro and non-pro tiers
    const aIsPro = a.tier === 'pro';
    const bIsPro = b.tier === 'pro';
    
    // Pro tier comes before non-pro tier
    if (aIsPro && !bIsPro) return -1;
    if (!aIsPro && bIsPro) return 1;
    
    // Within same tier, check if they have ratings
    const aHasRating = a.rating && a.rating > 0;
    const bHasRating = b.rating && b.rating > 0;
    
    // Both have ratings - sort by rating (highest first)
    if (aHasRating && bHasRating) {
      return b.rating - a.rating;
    }
    
    // Only one has rating - rated mentor comes first
    if (aHasRating && !bHasRating) return -1;
    if (!aHasRating && bHasRating) return 1;
    
    // Neither has rating - sort alphabetically by first name
    return a.first_name.localeCompare(b.first_name);
    });

  // Only display real database mentors - NO fallback data
  const infiniteMentors = sortedMentors.length > 0 ? [
    ...sortedMentors,
    ...sortedMentors,
    ...sortedMentors
  ] : [];

  // Auto-cycle carousel every 4 seconds
  useEffect(() => {
    console.log('🎠 Mentor carousel useEffect - API:', !!mentorApi, 'Mentors:', infiniteMentors.length);
    
    if (!mentorApi || infiniteMentors.length === 0) {
      console.log('🎠 No mentor API or no mentors, skipping auto-cycle');
      return;
    }

    console.log('🎠 Setting up mentor carousel interval');
    const interval = setInterval(() => {
      console.log('🎠 Mentor carousel scrollNext triggered');
      mentorApi.scrollNext();
    }, 4000);

    return () => {
      console.log('🎠 Cleaning up mentor carousel interval');
      clearInterval(interval);
    };
  }, [mentorApi, infiniteMentors.length]);

  const handleBookMentor = (mentor: any) => {
    if (onBookMentor) {
      // If onBookMentor prop is provided, use it (for authenticated users)
      onBookMentor(mentor);
    } else {
      // Default behavior for public users - redirect to auth
      safeSetItem('pendingMentorBooking', mentor.id);
      navigate('/auth/student');
    }
  };

  if (loading && !propMentors) {
    return (
      <section className="py-12 px-4">
        <div className="container mx-auto">
          {showTitle && (
            <div className="text-center mb-10">
              <h3 className="text-4xl font-bold mb-4 text-glow">Meet Our Mentors</h3>
              <p className="text-xl text-muted-foreground">Loading real mentor profiles...</p>
            </div>
          )}
        </div>
      </section>
    );
  }

  if ((error && !propMentors) || infiniteMentors.length === 0) {
    return (
      <section className="py-12 px-4">
        <div className="container mx-auto">
          {showTitle && (
            <div className="text-center mb-10">
              <h3 className="text-4xl font-bold mb-4 text-glow">Meet Our Mentors</h3>
              <p className="text-xl text-muted-foreground">
                {infiniteMentors.length === 0 
                  ? "No mentors found matching your search."
                  : error || "No mentors available at this time. Please check back soon!"
                }
              </p>
            </div>
          )}
        </div>
      </section>
    );
  }


  return (
    <section className="py-12 px-0 sm:px-4 overflow-hidden">
      <div className="mx-auto max-w-6xl px-0 sm:px-4 overflow-hidden">
        {showTitle && (
          <div className="text-center mb-10">
          </div>
        )}
        
        <div className="relative max-w-6xl mx-auto overflow-hidden">
          <Carousel
            setApi={setMentorApi}
            opts={{
              align: "start",
              loop: true,
            }}
            className="w-full overflow-hidden"
          >
            <CarouselContent className="-ml-4">
              {infiniteMentors.map((mentor, index) => (
                 <CarouselItem key={`${mentor.id}-${index}`} className="pl-4 basis-[85%] sm:basis-1/2 lg:basis-1/3">
                   <Card className="w-full h-[620px] obsidian-card md:hover:scale-105 transition-transform duration-300 group">
                     <CardContent className="p-4 md:p-6 pb-8 flex flex-col h-full">
                          {/* Mobile Layout - Compact for small screens */}
                          <div className="md:hidden flex flex-col items-center h-full">
                           <div className="mb-4">
                             <div className="relative">
                               <div className="w-32 h-32 rounded-lg overflow-hidden bg-muted">
                                 <img
                                   src={mentor.profile_image_url || defaultAvatar}
                                   alt={`${mentor.first_name} ${mentor.last_name}`}
                                   className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                                   onError={(e) => {
                                     e.currentTarget.src = defaultAvatar;
                                   }}
                                 />
                                {mentor.discovery_call_enabled && (
                                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-primary/90 to-transparent py-1 text-center">
                                    <span className="text-[10px] font-bold text-primary-foreground tracking-wide">
                                      FREE CONSULT
                                    </span>
                                  </div>
                                )}
                               </div>
                               <MentorTierBadge tier={mentor.tier} mentorName={`${mentor.first_name} ${mentor.last_name}`} className="absolute -bottom-1 -right-1" />
                             </div>
                           </div>
                           
                           <div className="text-center mb-3">
                             <h4 className="font-bold text-lg text-energy mb-1">
                               {mentor.first_name} {mentor.last_name}
                             </h4>
                             <p
                               className="text-sm text-muted-foreground mb-1 min-h-[36px]"
                               style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                             >
                               {mentor.specialty}
                             </p>
                             
                             <div className="flex items-center justify-center gap-2 mb-2">
                               <div className="flex items-center space-x-1">
                                 <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                                 <span className="text-sm font-medium">{mentor.rating.toFixed(1)}</span>
                               </div>
                               <span className="text-xs text-muted-foreground">({mentor.total_sessions})</span>
                             </div>
                             
                           </div>

                           <div className="flex-1 flex flex-col w-full">
                             <div className="h-20 mb-3">
                               <h5 className="text-sm font-semibold text-muted-foreground mb-1">Credits & Accolades</h5>
                               {mentor.credits && mentor.credits.length > 0 ? (
                                 <TruncatedCredits 
                                   credits={mentor.credits} 
                                   mentorId={mentor.id} 
                                   maxLines={4} 
                                 />
                               ) : (
                                 <p className="text-xs text-muted-foreground/60">No credits listed</p>
                               )}
                             </div>
                           </div>

                           <div className="pt-3 border-t border-border w-full">
                             <div className="text-center mb-3">
                               <div>
                                 <span className="text-lg font-bold text-destructive">
                                   ${(mentor.hourly_rate / 100).toFixed(0)}/hr
                                 </span>
                                 <span className="text-sm text-muted-foreground block">
                                   {(mentor.hourly_rate / 100).toFixed(0)} Tokens/hr
                                 </span>
                               </div>
                             </div>
                             
                             <div className="flex space-x-2">
                               <Button variant="outline" size="sm" className="flex-1 text-sm" asChild>
                                 <Link to={`/mentor/${mentor.id}`}>Profile</Link>
                               </Button>
                               <Button size="sm" className="flex-1 energy-button text-sm" onClick={() => handleBookMentor(mentor)}>
                                 <Calendar className="w-4 h-4 mr-1" />
                                 Book
                               </Button>
                             </div>
                           </div>
                         </div>

                          {/* Tablet Layout - Medium screens only */}
                          <div className="hidden md:flex lg:hidden flex-col items-center h-full">
                           <div className="mb-6">
                             <div className="relative">
                               <div className="w-40 h-40 rounded-lg overflow-hidden bg-muted">
                                 <img
                                   src={mentor.profile_image_url || defaultAvatar}
                                   alt={`${mentor.first_name} ${mentor.last_name}`}
                                   className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                                   onError={(e) => {
                                     e.currentTarget.src = defaultAvatar;
                                   }}
                                 />
                                {mentor.discovery_call_enabled && (
                                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-primary/90 to-transparent py-1.5 text-center">
                                    <span className="text-xs font-bold text-primary-foreground tracking-wide">
                                      FREE CONSULT
                                    </span>
                                  </div>
                                )}
                               </div>
                               <MentorTierBadge tier={mentor.tier} mentorName={`${mentor.first_name} ${mentor.last_name}`} className="absolute -bottom-1 -right-1" />
                             </div>
                           </div>
                           
                           <div className="text-center mb-4">
                             <h4 className="font-bold text-lg text-energy mb-1">
                               {mentor.first_name} {mentor.last_name}
                             </h4>
                             <p
                               className="text-sm text-muted-foreground mb-1 min-h-[40px]"
                               style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                             >
                               {mentor.specialty}
                             </p>
                             
                             <div className="flex items-center justify-center gap-2 mb-2">
                               <div className="flex items-center space-x-1">
                                 <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                                 <span className="text-sm font-medium">{mentor.rating.toFixed(1)}</span>
                               </div>
                               <span className="text-xs text-muted-foreground">({mentor.total_sessions})</span>
                             </div>
                             
                           </div>

                           <div className="flex-1 flex flex-col w-full">
                             <div className="h-24 mb-4">
                               <h5 className="text-sm font-semibold text-muted-foreground mb-2">Credits & Accolades</h5>
                               {mentor.credits && mentor.credits.length > 0 ? (
                                 <TruncatedCredits 
                                   credits={mentor.credits} 
                                   mentorId={mentor.id} 
                                   maxLines={4} 
                                 />
                               ) : (
                                 <p className="text-xs text-muted-foreground/60">No credits listed</p>
                               )}
                             </div>
                           </div>

                           <div className="pt-4 border-t border-border w-full">
                             <div className="text-center mb-3">
                               <div>
                                 <span className="text-lg font-bold text-destructive">
                                   ${(mentor.hourly_rate / 100).toFixed(0)}/hr
                                 </span>
                                 <span className="text-sm text-muted-foreground block">
                                   {(mentor.hourly_rate / 100).toFixed(0)} Tokens/hr
                                 </span>
                               </div>
                             </div>
                             
                             <div className="flex space-x-2">
                               <Button variant="outline" size="sm" className="flex-1 text-sm" asChild>
                                 <Link to={`/mentor/${mentor.id}`}>Profile</Link>
                               </Button>
                               <Button size="sm" className="flex-1 energy-button text-sm" onClick={() => handleBookMentor(mentor)}>
                                 <Calendar className="w-4 h-4 mr-1" />
                                 Book
                               </Button>
                             </div>
                           </div>
                         </div>

                          {/* Desktop Layout - Large screens only */}
                          <div className="hidden lg:flex lg:flex-col lg:items-center lg:h-full">
                          <div className="mb-6">
                           <div className="relative">
                             <div className="w-40 h-40 rounded-lg overflow-hidden bg-muted">
                               <img
                                 src={mentor.profile_image_url || defaultAvatar}
                                 alt={`${mentor.first_name} ${mentor.last_name}`}
                                 className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                                 onError={(e) => {
                                   e.currentTarget.src = defaultAvatar;
                                 }}
                               />
                              {mentor.discovery_call_enabled && (
                                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-primary/90 to-transparent py-1.5 text-center">
                                  <span className="text-xs font-bold text-primary-foreground tracking-wide">
                                    FREE CONSULT
                                  </span>
                                </div>
                              )}
                             </div>
                             <MentorTierBadge tier={mentor.tier} mentorName={`${mentor.first_name} ${mentor.last_name}`} className="absolute -bottom-1 -right-1" />
                           </div>
                         </div>
                         
                         <div className="text-center mb-4">
                           <h4 className="font-bold text-lg text-energy mb-1">
                             {mentor.first_name} {mentor.last_name}
                           </h4>
                           <p
                             className="text-sm text-muted-foreground mb-1 min-h-[40px]"
                             style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                           >
                             {mentor.specialty}
                           </p>
                           
                           <div className="flex items-center justify-center gap-2 mb-2">
                             <div className="flex items-center space-x-1">
                               <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                               <span className="text-sm font-medium">{mentor.rating.toFixed(1)}</span>
                             </div>
                             <span className="text-xs text-muted-foreground">({mentor.total_sessions})</span>
                           </div>
                           
                         </div>

                          <div className="flex-1 flex flex-col w-full">
                            <div className="h-24 mb-4">
                              <h5 className="text-sm font-semibold text-muted-foreground mb-2">Credits & Accolades</h5>
                              {mentor.credits && mentor.credits.length > 0 ? (
                                <TruncatedCredits 
                                 credits={mentor.credits} 
                                 mentorId={mentor.id} 
                                 maxLines={4} 
                               />
                              ) : (
                                <p className="text-xs text-muted-foreground/60">No credits listed</p>
                              )}
                            </div>
                          </div>

                          <div className="pt-4 border-t border-border w-full">
                           <div className="text-center mb-3">
                             <div>
                               <span className="text-lg font-bold text-destructive">
                                 ${(mentor.hourly_rate / 100).toFixed(0)}/hr
                               </span>
                               <span className="text-sm text-muted-foreground block">
                                 {(mentor.hourly_rate / 100).toFixed(0)} Tokens/hr
                               </span>
                             </div>
                           </div>
                           
                           <div className="flex space-x-2">
                             <Button variant="outline" size="sm" className="flex-1 text-sm" asChild>
                               <Link to={`/mentor/${mentor.id}`}>Profile</Link>
                             </Button>
                             <Button size="sm" className="flex-1 energy-button text-sm" onClick={() => handleBookMentor(mentor)}>
                               <Calendar className="w-4 h-4 mr-1" />
                               Book
                             </Button>
                           </div>
                         </div>
                       </div>
                     </CardContent>
                   </Card>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="hidden md:flex energy-button border-primary/30 bg-black/50 hover:bg-primary/20" />
            <CarouselNext className="hidden md:flex energy-button border-primary/30 bg-black/50 hover:bg-primary/20" />
          </Carousel>
        </div>

        {showTitle && (
          <div className="mt-8 text-center">
            <Button variant="outline" asChild>
              <Link to="/mentors">
                View All Mentors
              </Link>
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};

export default MentorCarousel;